import { Platform } from 'react-native';
import { apiUrl } from './apiService';
import type { TempOnboardingData } from './tempOnboardingService';

export interface OnboardingClinic { id: number; name: string }

export class ClinicSelectionError extends Error {
  constructor(public readonly code: string) { super(code); }
}

export function clinicSelectionPayload(data: TempOnboardingData) {
  if (data.clinicSelectionProtocol !== 'selection_v1' ||
      !Number.isSafeInteger(data.selectedClinicId) || (data.selectedClinicId ?? 0) < 1) {
    throw new ClinicSelectionError('CLINIC_SELECTION_REQUIRED');
  }
  return { clinic_selection_protocol: 'selection_v1', selected_clinic_id: data.selectedClinicId };
}

export function clinicCompletionError(code?: string, status?: number): ClinicSelectionError | undefined {
  if (status === 429 || code === 'RATE_LIMITED') return new ClinicSelectionError('ONBOARDING_RATE_LIMITED');
  if (status === 404) return new ClinicSelectionError('CLINIC_SERVER_NOT_READY');
  if (['CLINIC_SELECTION_INVALID', 'CLINIC_SELECTION_UNAVAILABLE'].includes(code ?? '')) {
    return new ClinicSelectionError('CLINIC_SELECTION_UNAVAILABLE');
  }
  if (['clinic_selection_required', 'CLINIC_SELECTION_REQUIRED'].includes(code ?? '')) {
    return new ClinicSelectionError('CLINIC_SELECTION_REQUIRED');
  }
  if (['invitation_revision_conflict', 'onboarding_profile_conflict'].includes(code ?? '')) {
    return new ClinicSelectionError('ONBOARDING_RETRY_REQUIRED');
  }
  if (['onboarding_incomplete', 'invalid_onboarding_payload'].includes(code ?? '')) {
    return new ClinicSelectionError('ONBOARDING_INCOMPLETE');
  }
  if (code?.startsWith('CLINIC_CATALOG_')) return new ClinicSelectionError('CLINIC_CATALOG_UNAVAILABLE');
  if (code === 'onboarding_service_unavailable') return new ClinicSelectionError('ONBOARDING_UNAVAILABLE');
  return undefined;
}

export function clinicCompletionFeedback(code: string) {
  const messages: Record<string, string> = {
    CLINIC_SERVER_NOT_READY: 'serverNotReady', CLINIC_SELECTION_REQUIRED: 'required',
    CLINIC_SELECTION_UNAVAILABLE: 'unavailable', CLINIC_CATALOG_UNAVAILABLE: 'loadError',
    ONBOARDING_RETRY_REQUIRED: 'retryCompletion', ONBOARDING_INCOMPLETE: 'incomplete',
    ONBOARDING_UNAVAILABLE: 'completeError',
    ONBOARDING_RATE_LIMITED: 'rateLimited',
  };
  return {
    messageKey: `onboarding.clinic.${messages[code] ?? 'completeError'}`,
    route: code === 'ONBOARDING_INCOMPLETE' ? '/onboarding-name' as const :
      ['CLINIC_SELECTION_REQUIRED', 'CLINIC_SELECTION_UNAVAILABLE'].includes(code) ? '/onboarding-clinic' as const : null,
    actionKey: code === 'ONBOARDING_INCOMPLETE' ? 'onboarding.clinic.resume' : 'onboarding.clinic.choose',
  };
}

async function catalog(requireCompletionSupport: boolean, requireEmailCompletion = false): Promise<OnboardingClinic[]> {
  // Explicit exception for the PUBLIC catalog in the local web preview only.
  // Account creation/Google completion NEVER use this development proxy.
  const url = __DEV__ && Platform.OS === 'web' && !requireCompletionSupport
    ? '/api/onboarding/clinics' : apiUrl('/api/onboarding/clinics');
  const controller = new AbortController();
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      (async () => {
        const response = await fetch(url, { signal: controller.signal, headers: { Accept: 'application/json' }, cache: 'no-store' });
        if (!response.ok) {
          throw new ClinicSelectionError(requireCompletionSupport && response.status === 404
            ? 'CLINIC_SERVER_NOT_READY' : 'CLINIC_CATALOG_UNAVAILABLE');
        }
        const body = await response.json();
        if (body?.success !== true || !Array.isArray(body.data?.clinics)) {
          throw new ClinicSelectionError('CLINIC_CATALOG_UNAVAILABLE');
        }
        if (requireCompletionSupport && (body.data.selection_protocol !== 'selection_v1' ||
            (requireEmailCompletion && body.data.authenticated_email_completion !== true))) {
          throw new ClinicSelectionError('CLINIC_SERVER_NOT_READY');
        }
        const seen = new Set<number>();
        return body.data.clinics.map((item: OnboardingClinic): OnboardingClinic => {
          if (!Number.isSafeInteger(item?.id) || item.id < 1 || typeof item.name !== 'string' ||
              !item.name.trim() || item.name.length > 200 || seen.has(item.id)) {
            throw new ClinicSelectionError('CLINIC_CATALOG_UNAVAILABLE');
          }
          seen.add(item.id);
          return { id: item.id, name: item.name.trim() };
        });
      })(),
      new Promise<never>((_, reject) => {
        timer = setTimeout(() => {
          controller.abort();
          reject(new ClinicSelectionError('CLINIC_CATALOG_UNAVAILABLE'));
        }, 10000);
      }),
    ]);
  } catch (error) {
    if (error instanceof ClinicSelectionError) throw error;
    throw new ClinicSelectionError('CLINIC_CATALOG_UNAVAILABLE');
  } finally {
    clearTimeout(timer);
  }
}

export const loadOnboardingClinics = () => catalog(false);
// Prevent an older published server from silently defaulting a new client to ID 1.
export const ensureClinicCompletionSupport = (method?: 'email_session') => catalog(true, method === 'email_session');
